import { gql, GraphQLClient } from 'graphql-request';
import { get } from 'lodash';

import { GraphSystemState, GraphTransactionData, GraphVaultOperation } from './vesta/types';

// Query must take in $first and $skip to work properly
export async function getAllQuery<Type>({
  query,
  client,
  queryParams,
  combineKey,
  queryLength = 1000,
}: {
  query: string;
  client: GraphQLClient;
  queryParams: any;
  combineKey: string;
  queryLength?: number;
}): Promise<Type[]> {
  let hasMoreItems = true;
  const allQueries = [];
  let skip = 0;

  while (hasMoreItems) {
    const res = await client.request(query, {
      ...queryParams,
      first: queryLength,
      skip,
    });

    const desiredArr = get(res, combineKey);

    allQueries.push(...desiredArr);

    if (desiredArr.length === queryLength) {
      skip += queryLength;
    } else {
      hasMoreItems = false;
    }
  }

  return allQueries;
}

export const transactionQuery = `
  transaction {
      id
      sequenceNumber
      blockNumber
      timestamp
  }
`;

export const systemStateQuery = `
  systemStateBefore {
      id
      asset
      sequenceNumber
      price
      totalCollateral
      totalDebt
      totalCollateralRatio
      tokensInStabilityPool
  }
  systemStateAfter {
      id
      asset
      sequenceNumber
      price
      totalCollateral
      totalDebt
      totalCollateralRatio
      tokensInStabilityPool
  }
`;

export const redemptionQuery = `
  redemption {
    id
    asset { 
      name
    }
    sequenceNumber
    redeemer { 
        id 
    }
    tokensAttemptedToRedeem
    tokensActuallyRedeemed
    collateralRedeemed
    partial
    fee
    ${transactionQuery}
  }
`;

export const liquidationQuery = `
  liquidation {
    id
    asset { 
      name
    }
    sequenceNumber
    liquidator { 
        id 
    }
    liquidatedDebt
    liquidatedCollateral
    collGasCompensation
    tokenGasCompensation
    ${transactionQuery}
  }
`;

export interface GraphLiquidationData {
  id: string;
  sequenceNumber: number;
  asset: { name: string };
  liquidator: { id: string };
  liquidatedDebt: string;
  liquidatedCollateral: string;
  collGasCompensation: string;
  tokenGasCompensation: string;
  transaction: GraphTransactionData;
}

export interface GraphRedemptionData {
  id: string;
  sequenceNumber: number;
  asset: { name: string };
  redeemer: { id: string };
  tokensAttemptedToRedeem: string;
  tokensActuallyRedeemed: string;
  collateralRedeemed: string;
  partial: boolean;
  fee: string;
  transaction: GraphTransactionData;
}

export interface GraphTroveChange {
  trove: { id: string };
  sequenceNumber: number;
  troveOperation: GraphVaultOperation;
  collateralBefore: string;
  collateralAfter: string;
  debtBefore: string;
  debtAfter: string;
  borrowingFee: string;
  transaction: GraphTransactionData;
  systemStateAfter: GraphSystemState;
  systemStateBefore: GraphSystemState;
  liquidation?: GraphLiquidationData;
  redemption?: GraphRedemptionData;
}

export const queryVaultChangeState = gql`
  query get_vault_changes($first: Int, $skip: Int, $userAddress: String){
    troves(where: { owner_contains: $userAddress }) {
      asset {
        name
      }
      changes(first: $first, skip: $skip) {
        sequenceNumber
        troveOperation
        collateralBefore
        collateralAfter
        debtBefore
        debtAfter
        borrowingFee
        ${transactionQuery}
        ${systemStateQuery}
        ${redemptionQuery}
        ${liquidationQuery}
      }
    }
  }
`;
